import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { CheckCircle, Clock, CopyCheck, DraftingCompass, FileText, Flame, LayoutDashboard, Sigma } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NotificationSound } from '../../CommonComponents/CommonNotification';
import { ApiCall, getDateAndTime, handleDateChange } from '../../CommonComponents/CustomHooks';
import { CommonApiData, initializeFilterData, virtualPath } from '../../CommonComponents/EnvConfig';
import { handleDownloadExcel } from '../../CommonComponents/ExportData';
import { DateBox, FormButton } from '../../CommonComponents/InputBox';
import { Table } from '../../CommonComponents/Table';
import { setSubLoading } from '../../Redux/Services/AuthSlice';

const LeadDashboard = () => {
    const inputrefs = useRef([])
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [tableData, setTableData] = useState([])
    const [cardData, setCardData] = useState({});
    const [currentPageNo, setCurrentPageNo] = useState(1);
    const [filterData, setFilterData] = useState(initializeFilterData)
    const [sortConfig, setSortConfig] = useState({ SortColumn: null, SortOrder: null });
    const InitialState = {
        "fromDate": getDateAndTime(7).date,
        "toDate": getDateAndTime().date,
    }
    const [showCards, setShowCards] = useState(false)
    const [showModalBox, setShowModalBox] = useState(false)
    const [selectedCardDetails, setSelectedCardDetails] = useState({})
    const [exportData, setExportData] = useState({
        CallType: ""
    });


    const handleClear = () => {
        setFormData(InitialState)
        inputrefs.current['txtFromDate'].focus()
        //setShowCards(false)
    }

    const handleCardClick = async (CurrentPage = currentPageNo, stats) => {
        let PageIndex = 1
        if (CurrentPage !== undefined && typeof (CurrentPage) !== "object") {
            PageIndex = CurrentPage;
            setCurrentPageNo(CurrentPage);
        }
        else {
            setCurrentPageNo(1);
        }
        const data = {
            ...formData,
            ...filterData,
            CallType: stats?.key || "",
            PageIndex: `${PageIndex}`,
            ...sortConfig,
        };
        setExportData(prev => ({
            ...prev,
            CallType: stats?.key || ""
        }));
        setIsLoading(true)
        let res = await ApiCall(`/api/Dashboard/LeadDashboardDetail`, data)
        if (res.Status === 'SUCCESS') {
            setTableData(res);
            setSelectedCardDetails(stats)
            setShowModalBox(true)
        } else if (res.Status === 'ERROR') {
            setTableData([]);
            setSelectedCardDetails({})
        }
        setIsLoading(false)
        if (res.Status === 'EXPIRED') {
            await NotificationSound(res.Status, res.Message, res.focus, inputrefs);
        }
    }


    const handleModalDocShowClose = async () => {
        await setShowModalBox(false)
        await setSelectedCardDetails({})
    };

    const handleSearch = async () => {
        setShowCards(true)
        const data = {
            ...formData,
            ...filterData,
            CallType: '',
            ...sortConfig
        }
        setIsLoading(true)
        let res = await ApiCall(`/api/Dashboard/LeadDashboard`, data)
        if (res.status === 'SUCCESS') {
            setCardData(res.data);
        }
        else if (res.status === 'ERROR') {
            setCardData({});
        }
        setIsLoading(false)
        if (res.status === 'EXPIRED') {
            await NotificationSound(res.status, res.message, res.focus, inputrefs);
        }
    }


    const handleChange1 = (event, value) => {
        handleCardClick(value, selectedCardDetails);
    };



    const handleSorting = (SortColumn) => {
        let SortOrder = 'ASC';
        if (sortConfig && sortConfig.SortColumn === SortColumn && sortConfig.SortOrder === 'ASC') {
            SortOrder = 'DESC';
        }
        setSortConfig({ SortColumn, SortOrder });
        handleCardClick(currentPageNo, selectedCardDetails);
    };


    const handleFollowUp = (value) => {
        window.open(`${virtualPath}/LeadEntry/LeadEntryFollowUp?LeadNo=${value}`, "_blank");
    }


    const [formData, setFormData] = useState(InitialState)


    const cardsConfig = [
        { key: 'totalLeads', label: 'Total Leads', icon: FileText, color: 'bg-red-300' },
        { key: 'followUp', label: 'Follow Up', icon: Clock, color: 'bg-sky-300' },
        { key: 'closed', label: 'Closed', icon: CheckCircle, color: 'bg-blue-300' },
        { key: 'qualified', label: 'Qualified', icon: FileText, color: 'bg-purple-300' },
        { key: 'duplicate', label: 'Duplicates', icon: CopyCheck, color: 'bg-green-300' },
        { key: 'unAssigned', label: 'Un Assigned', icon: Flame, color: 'bg-yellow-300' },
    ];

    const dueCardsConfig = [
        { key: 'dueFollowup', label: 'Due Follow Up', icon: Sigma, color: 'bg-orange-400' },
        { key: 'overDueFollowup', label: 'Overdue Follow Up', icon: DraftingCompass, color: 'bg-red-400' },
    ];

    const extraPayload = {
        ...filterData,
        ...formData,
        "loginCompanyCode": CommonApiData().companyCode,
    }


    useEffect(() => {
        inputrefs.current['txtFromDate'].focus()
        document.title = 'Lead Dashboard'
        handleSearch()
    }, []);

    return (
        <>
            {/* Header Section */}
            <div className="bg-white shadow-sm rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <LayoutDashboard className="w-6 h-6 text-teal-600" />
                        <h2 className="text-xl font-semibold text-gray-800">
                            Lead Dashboard
                        </h2>
                    </div>
                </div>
                <div className='row mt-2'>
                    <DateBox
                        inputrefs={inputrefs}
                        label="From Date"
                        placeholder="DD-MMM-YYYY"
                        id='txtFromDate'
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                inputrefs.current['txtToDate'].focus()
                            };
                        }}
                        selected={formData.fromDate === '' ? '' : new Date(formData.fromDate)}
                        onChange={(e) => handleDateChange(e, setFormData, formData, 'fromDate')}
                    />


                    <DateBox
                        inputrefs={inputrefs}
                        label="To Date"
                        placeholder="DD-MMM-YYYY"
                        id='txtToDate'
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                inputrefs.current['btnSub'].focus()
                            };
                        }}
                        selected={formData.toDate === '' ? '' : new Date(formData.toDate)}
                        onChange={(e) => handleDateChange(e, setFormData, formData, 'toDate')}
                    />

                </div>

                <div className='card-body'>
                    <FormButton
                        id={'btnSub'}
                        inputrefs={inputrefs}
                        handleSearch={handleSearch}
                        save={false}
                        search={true}
                        Export={false}
                        handleClear={handleClear}
                    />
                </div>
            </div>

            {showCards && cardData && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-10">
                        {cardsConfig.map((stat, index) => {
                            const Icon = stat.icon;
                            return (
                                <div
                                    key={index}
                                    onClick={() => {
                                        handleCardClick(currentPageNo, stat)

                                    }}
                                    className="bg-white cursor-pointer rounded-lg shadow p-3 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <p className="text-gray-500 text-xs font-medium">
                                                {stat.label}
                                            </p>

                                            <p className="text-2xl font-bold mt-1 text-gray-800">
                                                {cardData[stat.key] || 0}
                                            </p>

                                        </div>

                                        <div className={`${stat.color} p-3 rounded-lg`}>
                                            <Icon size={24} className="text-white" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                        {dueCardsConfig.map((stat, index) => {
                            const Icon = stat.icon;
                            return (
                                <div
                                    key={index}
                                    onClick={() => {
                                        handleCardClick(currentPageNo, stat)

                                    }}
                                    className="bg-white cursor-pointer rounded-lg shadow p-3 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <p className="text-gray-500 text-xs font-medium">
                                                {stat.label}
                                            </p>

                                            <p className="text-2xl font-bold mt-1 text-gray-800">
                                                {cardData[stat.key] || 0}
                                            </p>

                                        </div>

                                        <div className={`${stat.color} p-3 rounded-lg`}>
                                            <Icon size={24} className="text-white" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            <Dialog
                open={showModalBox}
                onClose={handleModalDocShowClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth={false}
                sx={{
                    "& .MuiDialog-container": {
                        "& .MuiPaper-root": {
                            width: "100%",
                            maxWidth: "1000px",
                        }

                    },
                    "& .css-hz1bth-MuiDialog-container": {
                        alignItems: 'center'
                    },
                    "& .MuiPaper-root": {
                        width: '95vw',
                        maxWidth: '1000px',
                        borderRadius: '8px',
                    },
                }}>

                <DialogTitle
                    id="alert-dialog-title"
                    className="p-3 border-bottom d-flex justify-content-between align-items-center"
                >
                    <div style={{ fontWeight: 500 }}> {selectedCardDetails?.label || "Total Lead Details"} </div>
                    <div className="d-flex align-items-center gap-3">
                        <FormButton
                            divclassname="items-end"
                            id={'btnSub'}
                            inputrefs={inputrefs}
                            handleExport={async () => {
                                const payload = {
                                    ...extraPayload,
                                    ...exportData,
                                };
                                await dispatch(setSubLoading(true))
                                handleDownloadExcel(`/api/Dashboard/LeadDashboardDetail`, payload, 'Lead Dashboard')
                                await dispatch(setSubLoading(false))
                            }}
                            save={false}
                            search={false}
                            Export={true}
                            Reset={false}
                        />
                        <i
                            className="fa-solid fa-xmark"
                            onClick={handleModalDocShowClose}
                            title="Close"
                            style={{ cursor: 'pointer', fontSize: '20px', padding: '2px', borderRadius: '5px' }}
                        />
                    </div>
                </DialogTitle>


                <DialogContent className='main-card m-2 p-2' >

                    <Table
                        loading={isLoading}
                        PageName={'LeadDashboard'}
                        handleFilter={() => {
                            handleCardClick(currentPageNo, selectedCardDetails)
                            setFilterData({
                                ...filterData,
                            })
                        }}
                        filterData={filterData}
                        tableData={tableData}
                        setFilterData={setFilterData}
                        currentPageNo={currentPageNo}
                        handleChange1={handleChange1}
                        handleSorting={handleSorting}
                        sortConfig={sortConfig}
                        matchingkey={'LeadNo'}
                        ClickOnLink={handleFollowUp}
                        Filter={false}
                        onlyFilterPage={true}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
}

export default LeadDashboard;